export const DELIVERY_STATUS = Object.freeze({
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  NOT_APPROVED: 'NOT_APPROVED',
});

export const EVIDENCE_STATUS = Object.freeze({
  NOT_SUBMITTED: 'NOT_SUBMITTED',
  UNDER_REVIEW: 'UNDER_REVIEW',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
});

export const RESULT_STATUS = Object.freeze({
  DRAFT: 'DRAFT',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED',
});

export const RESULT_STATUS_OPTIONS = Object.freeze(Object.values(RESULT_STATUS));

export const resultStatusLabelKey = status => (
  `climateAction.annualVerification.${String(status || RESULT_STATUS.DRAFT).toLowerCase()}`
);

export const DELIVERY_STATUS_OPTIONS = Object.freeze([
  DELIVERY_STATUS.DRAFT,
  DELIVERY_STATUS.IN_PROGRESS,
  DELIVERY_STATUS.COMPLETED,
  DELIVERY_STATUS.CANCELLED,
  DELIVERY_STATUS.NOT_APPROVED,
]);

export const EVIDENCE_STATUS_OPTIONS = Object.freeze([
  EVIDENCE_STATUS.NOT_SUBMITTED,
  EVIDENCE_STATUS.UNDER_REVIEW,
  EVIDENCE_STATUS.VERIFIED,
  EVIDENCE_STATUS.REJECTED,
]);

export const DELIVERY_STATUS_TRANSITIONS = Object.freeze({
  [DELIVERY_STATUS.DRAFT]: Object.freeze([
    DELIVERY_STATUS.IN_PROGRESS,
    DELIVERY_STATUS.NOT_APPROVED,
  ]),
  [DELIVERY_STATUS.IN_PROGRESS]: Object.freeze([
    DELIVERY_STATUS.COMPLETED,
    DELIVERY_STATUS.NOT_APPROVED,
  ]),
  [DELIVERY_STATUS.COMPLETED]: Object.freeze([]),
  [DELIVERY_STATUS.NOT_APPROVED]: Object.freeze([DELIVERY_STATUS.DRAFT]),
});

const DELIVERY_KEYS = Object.freeze({
  [DELIVERY_STATUS.DRAFT]: 'draft',
  [DELIVERY_STATUS.IN_PROGRESS]: 'inProgress',
  [DELIVERY_STATUS.COMPLETED]: 'completed',
  [DELIVERY_STATUS.CANCELLED]: 'cancelled',
  [DELIVERY_STATUS.NOT_APPROVED]: 'notApproved',
});

const EVIDENCE_KEYS = Object.freeze({
  [EVIDENCE_STATUS.NOT_SUBMITTED]: 'notSubmitted',
  [EVIDENCE_STATUS.UNDER_REVIEW]: 'underReview',
  [EVIDENCE_STATUS.VERIFIED]: 'verified',
  [EVIDENCE_STATUS.REJECTED]: 'rejected',
});

const LEGACY_DELIVERY_STATUS = Object.freeze({
  APPROVED: DELIVERY_STATUS.IN_PROGRESS,
  EVIDENCE_PENDING: DELIVERY_STATUS.IN_PROGRESS,
  PROPOSED: DELIVERY_STATUS.DRAFT,
  REJECTED: DELIVERY_STATUS.NOT_APPROVED,
  VERIFIED: DELIVERY_STATUS.COMPLETED,
  approved: DELIVERY_STATUS.IN_PROGRESS,
  completed: DELIVERY_STATUS.COMPLETED,
  evidencePending: DELIVERY_STATUS.IN_PROGRESS,
  inProgress: DELIVERY_STATUS.IN_PROGRESS,
  proposed: DELIVERY_STATUS.DRAFT,
  rejected: DELIVERY_STATUS.NOT_APPROVED,
  saved: DELIVERY_STATUS.DRAFT,
  verified: DELIVERY_STATUS.COMPLETED,
});

export const canonicalDeliveryStatus = (status, fallback = DELIVERY_STATUS.DRAFT) => {
  if (DELIVERY_STATUS_OPTIONS.includes(status)) return status;
  return LEGACY_DELIVERY_STATUS[status] || fallback;
};

export const canonicalEvidenceStatus = (status) => {
  if (EVIDENCE_STATUS_OPTIONS.includes(status)) return status;
  return EVIDENCE_STATUS.NOT_SUBMITTED;
};

export const deliveryStatusForAction = action => canonicalDeliveryStatus(
  action?.actionStatus || action?.workflowStatus || action?.workflow_status || action?.status,
);

export const evidenceStatusForResult = result => canonicalEvidenceStatus(
  result?.verificationStatus || result?.verification_status,
);

export const deliveryStatusKey = status => (
  DELIVERY_KEYS[canonicalDeliveryStatus(status)] || DELIVERY_KEYS[DELIVERY_STATUS.DRAFT]
);

export const evidenceStatusKey = status => (
  EVIDENCE_KEYS[canonicalEvidenceStatus(status)]
  || EVIDENCE_KEYS[EVIDENCE_STATUS.NOT_SUBMITTED]
);

export const deliveryStatusLabelKey = status => (
  `climateAction.status.${deliveryStatusKey(status)}`
);

export const evidenceStatusLabelKey = status => (
  `climateAction.annualVerification.${evidenceStatusKey(status)}`
);

/** Current state plus its legal next states, in the product's stable display order. */
export function deliveryStatusOptions(currentStatus = DELIVERY_STATUS.DRAFT, isNew = false) {
  const canonicalStatus = canonicalDeliveryStatus(currentStatus);
  if (isNew) return [DELIVERY_STATUS.DRAFT];
  return [
    canonicalStatus,
    ...(DELIVERY_STATUS_TRANSITIONS[canonicalStatus] || []),
  ];
}

export const deliveryTransitionIsValid = (currentStatus, nextStatus, isNew = false) => {
  const canonicalNextStatus = canonicalDeliveryStatus(nextStatus, null);
  return canonicalNextStatus !== null
    && deliveryStatusOptions(currentStatus, isNew).includes(canonicalNextStatus);
};

/** Current/no-op is retained when it is shared with another row's legal transition. */
export function commonDeliveryStatusOptions(currentStatuses = []) {
  if (currentStatuses.length === 0) return [];
  const normalizedStatuses = currentStatuses.map(canonicalDeliveryStatus);
  const [firstStatus, ...remainingStatuses] = normalizedStatuses;
  return deliveryStatusOptions(firstStatus).filter(candidateStatus => (
    remainingStatuses.every(status => deliveryStatusOptions(status).includes(candidateStatus))
  ));
}

/** Return delivery destinations that are legal for every selected current state. */
export function commonDeliveryTransitionOptions(currentStatuses = []) {
  const normalizedStatuses = currentStatuses.map(canonicalDeliveryStatus);
  return commonDeliveryStatusOptions(normalizedStatuses).filter(candidateStatus => (
    !normalizedStatuses.every(currentStatus => currentStatus === candidateStatus)
  ));
}

/** Build a complete ordered select list while disabling unavailable transitions. */
export function statusSelectOptions(statuses, enabledStatuses, labelKeyFor) {
  const enabled = new Set(enabledStatuses);
  return statuses.map(status => ({
    disabled: !enabled.has(status),
    labelKey: labelKeyFor(status),
    status,
  }));
}
